import React from 'react';
import { Users, Play, Pause, Plus, Save, X, Target, MessageSquare, TrendingUp, Bot, Clock, Send, Settings, BarChart3, RefreshCw, Loader2, Zap } from 'lucide-react';

export function SchedulerTab({ 
  config, 
  status, 
  queueStats, 
  history,
  segments,
  saving,
  onConfigChange, 
  onSaveConfig, 
  onStart, 
  onStop, 
  onDeduplicate,
  onRefresh 
}) {
  const isRunning = config?.enabled || false;
  const todaySent = status?.today_stats?.sent || 0;
  const dailyLimit = config?.daily_limit || 50;
  const progress = dailyLimit > 0 ? (todaySent / dailyLimit) * 100 : 0;
  
  const updateConfig = (field, value) => {
    onConfigChange({ ...config, [field]: value });
  };
  
  const updateWorkingHours = (field, value) => {
    onConfigChange({
      ...config,
      working_hours: { ...config?.working_hours, [field]: value }
    });
  };
  
  const toggleWorkingDay = (day) => {
    const days = config?.working_days || [0, 1, 2, 3, 4];
    const newDays = days.includes(day) 
      ? days.filter(d => d !== day)
      : [...days, day].sort();
    updateConfig('working_days', newDays);
  };
  
  const dayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
  
  return (
    <div className="space-y-6">
      {/* Status Principal */}
      <div className={`p-6 rounded-xl border-2 ${
        isRunning 
          ? 'bg-green-500/10 border-green-500/50' 
          : 'bg-gray-800/50 border-gray-700/50'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-full ${isRunning ? 'bg-green-500/20' : 'bg-gray-700/50'}`}>
              {isRunning ? (
                <Zap size={32} className="text-green-400 animate-pulse" />
              ) : (
                <Pause size={32} className="text-gray-400" />
              )}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                Piloto Automático {isRunning ? 'LIGADO' : 'DESLIGADO'}
              </h2>
              <p className="text-gray-400">
                {isRunning 
                  ? 'O sistema está enviando mensagens automaticamente' 
                  : 'Clique em Iniciar para começar os envios automáticos'
                }
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onRefresh}
              className="p-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-gray-300"
              title="Atualizar"
            >
              <RefreshCw size={20} />
            </button>
            
            {isRunning ? (
              <button
                onClick={onStop}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold"
              >
                <Pause size={20} />
                PARAR
              </button>
            ) : (
              <button
                onClick={onStart}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold"
              >
                <Play size={20} />
                INICIAR
              </button>
            )}
          </div>
        </div>
        
        {/* Progresso do dia */}
        {isRunning && (
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Progresso de Hoje</span>
              <span className="text-white font-medium">{todaySent} / {dailyLimit} mensagens</span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            {status?.scheduler?.last_activity && (
              <p className="text-gray-500 text-xs mt-2">
                Última atividade: {new Date(status.scheduler.last_activity).toLocaleString('pt-BR')}
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <Users className="text-blue-400" size={24} />
            <div>
              <p className="text-2xl font-bold text-white">{queueStats?.queue?.new || 0}</p>
              <p className="text-gray-400 text-sm">Na Fila</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <MessageSquare className="text-green-400" size={24} />
            <div>
              <p className="text-2xl font-bold text-white">{queueStats?.queue?.contacted || 0}</p>
              <p className="text-gray-400 text-sm">Contatados</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <TrendingUp className="text-yellow-400" size={24} />
            <div>
              <p className="text-2xl font-bold text-white">{queueStats?.queue?.interested || 0}</p>
              <p className="text-gray-400 text-sm">Interessados</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <Bot className="text-orange-400" size={24} />
            <div>
              <p className="text-2xl font-bold text-white">{queueStats?.queue?.bots || 0}</p>
              <p className="text-gray-400 text-sm">Bots Detectados</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Estimativa */}
      {queueStats?.total_in_queue > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Clock className="text-blue-400" size={24} />
            <div>
              <p className="text-white font-medium">
                Estimativa: {queueStats.days_to_complete} dias para processar toda a fila
              </p>
              <p className="text-gray-400 text-sm">
                {queueStats.total_in_queue} leads na fila × {queueStats.daily_limit} por dia
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Configurações */}
      
      {/* Mensagens de Abertura */}
      <div className="border border-green-500/30 rounded-xl p-5 bg-green-500/5">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Send size={20} className="text-green-400" />
          Mensagens de Abertura (variações)
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          O sistema escolhe aleatoriamente entre as variações ao enviar. Use {'{nome}'} para o nome do estabelecimento.
        </p>
        <div className="space-y-2">
          {(config?.opening_messages || ['']).map((msg, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-green-400 text-sm mt-2 w-6 flex-shrink-0">{i + 1}.</span>
              <textarea
                value={msg}
                onChange={(e) => {
                  const arr = [...(config?.opening_messages || [''])];
                  arr[i] = e.target.value;
                  onConfigChange({ ...config, opening_messages: arr });
                }}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm resize-none"
                rows={2}
                placeholder={
                  i === 0 ? "Ex: Olá! Tudo bem? Vocês trabalham com delivery?" :
                  i === 1 ? "Ex: Oi! Vi que vocês são do ramo alimentício. Fazem entregas?" :
                  "Ex: Boa tarde! Gostaria de saber se vocês fazem delivery."
                }
              />
              {(config?.opening_messages || ['']).length > 1 && (
                <button
                  onClick={() => {
                    const arr = (config?.opening_messages || ['']).filter((_, idx) => idx !== i);
                    onConfigChange({ ...config, opening_messages: arr });
                  }}
                  className="text-red-400 hover:text-red-300 p-1 self-start mt-1"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
          {(config?.opening_messages || ['']).length < 5 && (
            <button
              onClick={() => {
                const arr = [...(config?.opening_messages || ['']), ''];
                onConfigChange({ ...config, opening_messages: arr });
              }}
              className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1 mt-2"
            >
              <Plus size={14} /> Adicionar variação
            </button>
          )}
        </div>
      </div>

      {/* Mensagens de Follow-up */}
      <div className="border border-blue-500/30 rounded-xl p-5 bg-blue-500/5">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <MessageSquare size={20} className="text-blue-400" />
          Follow-up (após resposta)
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          Enviada automaticamente quando o lead responde a mensagem de abertura. Deixe em branco para usar IA.
        </p>
        <div className="space-y-2">
          {(config?.followup_messages || ['']).map((msg, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-blue-400 text-sm mt-2 w-6 flex-shrink-0">{i + 1}.</span>
              <textarea
                value={msg}
                onChange={(e) => {
                  const arr = [...(config?.followup_messages || [''])];
                  arr[i] = e.target.value;
                  onConfigChange({ ...config, followup_messages: arr });
                }}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm resize-none"
                rows={2}
                placeholder="Ex: Que legal! Podem me enviar o cardápio? Tenho uma proposta interessante para vocês."
              />
              {(config?.followup_messages || ['']).length > 1 && (
                <button
                  onClick={() => {
                    const arr = (config?.followup_messages || ['']).filter((_, idx) => idx !== i);
                    onConfigChange({ ...config, followup_messages: arr });
                  }}
                  className="text-red-400 hover:text-red-300 p-1 self-start mt-1"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
          {(config?.followup_messages || ['']).length < 3 && (
            <button
              onClick={() => {
                const arr = [...(config?.followup_messages || ['']), ''];
                onConfigChange({ ...config, followup_messages: arr });
              }}
              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-2"
            >
              <Plus size={14} /> Adicionar variação
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Segmentos Alvo */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target size={20} className="text-green-400" />
            Segmentos Alvo
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Selecione quais blocos de leads o piloto automático deve processar. Se nenhum for selecionado, envia para todos.
          </p>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {segments && segments.length > 0 ? segments.map(seg => {
              const isSelected = (config?.priority_segments || []).includes(seg.id);
              return (
                <label
                  key={seg.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    isSelected 
                      ? 'bg-green-500/20 border border-green-500/50' 
                      : 'bg-gray-700/30 border border-gray-700/50 hover:bg-gray-700/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      const current = config?.priority_segments || [];
                      const newSegments = isSelected 
                        ? current.filter(id => id !== seg.id)
                        : [...current, seg.id];
                      onConfigChange({ ...config, priority_segments: newSegments });
                    }}
                    className="w-4 h-4 rounded accent-green-500"
                  />
                  <span className={`text-sm font-medium ${isSelected ? 'text-green-300' : 'text-gray-300'}`}>
                    {seg.name}
                  </span>
                </label>
              );
            }) : (
              <p className="text-gray-500 text-sm">Nenhum segmento criado. Crie segmentos na aba &quot;Seg&quot;.</p>
            )}
          </div>
          
          {(config?.priority_segments || []).length > 0 && (
            <p className="text-green-400 text-xs mt-3">
              {config.priority_segments.length} segmento(s) selecionado(s)
            </p>
          )}
        </div>

        {/* Horários */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock size={20} className="text-purple-400" />
            Horário de Funcionamento
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Início</label>
                <input
                  type="time"
                  value={config?.working_hours?.start || '09:00'}
                  onChange={(e) => updateWorkingHours('start', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Fim</label>
                <input
                  type="time"
                  value={config?.working_hours?.end || '18:00'}
                  onChange={(e) => updateWorkingHours('end', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">Dias da Semana</label>
              <div className="flex gap-2">
                {dayNames.map((day, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleWorkingDay(idx)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      (config?.working_days || [0, 1, 2, 3, 4]).includes(idx)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Limites */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings size={20} className="text-blue-400" />
            Limites e Intervalos
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Limite Diário: {config?.daily_limit || 50} mensagens
              </label>
              <input
                type="range"
                min="10"
                max="200"
                step="10"
                value={config?.daily_limit || 50}
                onChange={(e) => updateConfig('daily_limit', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10</span>
                <span>200</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  Intervalo Mín: {config?.min_interval || 30}s
                </label>
                <input
                  type="range"
                  min="15"
                  max="120"
                  step="5"
                  value={config?.min_interval || 30}
                  onChange={(e) => updateConfig('min_interval', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  Intervalo Máx: {config?.max_interval || 60}s
                </label>
                <input
                  type="range"
                  min="30"
                  max="180"
                  step="10"
                  value={config?.max_interval || 60}
                  onChange={(e) => updateConfig('max_interval', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ações */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={onSaveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-xl font-medium"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          Salvar Configurações
        </button>
        
        <button
          onClick={onDeduplicate}
          className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium"
        >
          <RefreshCw size={18} />
          Remover Duplicatas
        </button>
      </div>
      
      {/* Histórico */}
      {history && history.length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-green-400" />
            Histórico de Envios (7 dias)
          </h3>
          
          <div className="grid grid-cols-7 gap-2">
            {history.slice(0, 7).reverse().map((day, idx) => (
              <div key={idx} className="text-center">
                <div className="h-24 bg-gray-700/50 rounded-lg relative overflow-hidden">
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-500 to-green-400/50"
                    style={{ 
                      height: `${Math.min((day.sent / (config?.daily_limit || 50)) * 100, 100)}%` 
                    }}
                  />
                </div>
                <p className="text-white font-medium mt-2">{day.sent}</p>
                <p className="text-gray-500 text-xs">
                  {new Date(day.date).toLocaleDateString('pt-BR', { weekday: 'short' })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== TAB DE CONVERSAS ====================
